/**
 * Les gestes du bilan de séance, ceux de l'ancien écran (`CloseOutScreen`, `StudentCloseOutCard`,
 * `LiveSatisfactionSection`) :
 *  - sur un élève : la note et ses étiquettes s'enregistrent 500 ms après le dernier toucher
 *    (changer la note remet les étiquettes à zéro), le mot écrit 1 s après la dernière frappe, le
 *    vocal à l'envoi de la feuille (au stockage, puis `comment_record` ; un bilan d'essai part sur
 *    `setStudentDiagnostic`) ; changer de mode efface l'autre mot, sans confirmation ; « Terminé »
 *    replie la carte ;
 *  - sur la plateforme : la note part aussitôt, les étiquettes et le ressenti écrit avec un délai, le
 *    vocal à l'envoi ; changer de mode avec un contenu demande d'abord confirmation ;
 *  - au pied : « Terminer la session » relit le compte (la tâche « bilans manquants » de l'accueil)
 *    et revient ; « Plus tard » confirme d'abord.
 *
 * Chaque écriture se mesure (`close_out.*`, surface `tutor_close_out`). Elles passent par `apiCall`
 * et non par `useApiMutation` : plusieurs cartes enregistrent en même temps à la frappe, sur la même
 * tranche (`sendStudentComment`, que la note, le mot et le vocal partagent) ; chaque appel doit
 * entendre SA réponse. Une machine qui porte plusieurs gestes : `useGestureEmitter`.
 *
 * Un enregistrement en attente part quand l'écran se ferme : l'ancien écran l'abandonnait.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { API_URL } from "../../../../../../data/constants/apiConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveReportService } from "../../../../../../data/services/liveReportService"
import { liveService } from "../../../../../../data/services/liveService"
import { vaporStore } from "../../../../../../data/services/vaporService"
import { fetchResourceFromURI } from "../../../../../../data/utility/fileFromURI"
import { apiCall, apiFailureOf, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import {
    closeOutSendError,
    isTrialOccurrence,
    platformTagsPositive,
    TEXT_NOT_SAVED,
    VOICE_NOT_SENT,
    type ApiCloseOutOccurrence,
} from "../../../../adapters/tutor/Lives/tutorCloseOutAdapter"
import {
    closeOutDraftsReducer,
    draftOf,
    EMPTY_DRAFTS,
    PLATFORM,
    studentTarget,
    targetKey,
    type CloseOutDraftAction,
    type CloseOutDrafts,
    type CloseOutTarget,
} from "../../../../view-models/tutor/Lives/tutorCloseOutDrafts"
import {
    platformTagChoices,
    studentTagChoices,
    tagKeyOf,
    type TutorCloseOutTagCatalogs,
} from "../../../../view-models/tutor/Lives/tutorCloseOutViewModel"
import { type UITutorCommentMode } from "../../../types/tutorCloseOut"
import { type GestureName, useGestureEmitter } from "../../../hooks/gestureTracking"
import { useBackOr } from "../../../hooks/useBackOr"
import { formatVoiceDuration } from "../../../formatters/voiceFormatter"

const SURFACE = "tutor_close_out"
/** Les délais de l'ancien écran : la note (`debounce` 500 ms), le texte (`AutoSaveTextInputM`, 1 s). */
const RATING_SAVE_MS = 500
const TEXT_SAVE_MS = 1000

type StudentId = string | number
type Pending = { fire: () => void; timer: ReturnType<typeof setTimeout> }
/** Un vocal fini, gardé jusqu'à ce qu'il arrive : « Réessayer » ne réenregistre pas, ni ne réenvoie au stockage. */
type PendingVoice = { file: VoiceFile; tmpKey: string | null }

const keyOf = (id: StudentId) => String(id)

export type TutorCloseOutStateArgs = {
    occurrenceId: string | number | null
    /** La séance du live d'où le bilan est poussé (`liveSessionId` de la route) ; absente depuis l'accueil. */
    liveSessionId?: string | number | null
    occurrence: ApiCloseOutOccurrence | null
    catalogs: TutorCloseOutTagCatalogs
}

export function useTutorCloseOutState({ occurrenceId, liveSessionId, occurrence, catalogs }: TutorCloseOutStateArgs) {
    const dispatch = useDispatch()
    const emit = useGestureEmitter()

    // ── Les brouillons : la vérité est dans le ref, l'état ne sert qu'au rendu ──
    const draftsRef = useRef<CloseOutDrafts>(EMPTY_DRAFTS)
    const [drafts, setDrafts] = useState<CloseOutDrafts>(EMPTY_DRAFTS)
    const apply = useCallback((action: CloseOutDraftAction) => {
        const next = closeOutDraftsReducer(draftsRef.current, action)
        if (next === draftsRef.current) return
        draftsRef.current = next
        setDrafts(next)
    }, [])
    useEffect(() => {
        if (occurrence != null) apply({ type: "seed", occurrence })
    }, [occurrence, apply])

    const occurrenceRef = useRef(occurrence)
    occurrenceRef.current = occurrence
    const occurrenceIdRef = useRef(occurrenceId)
    occurrenceIdRef.current = occurrenceId

    // ── Une écriture mesurée, qui entend sa propre réponse ──
    const write = useCallback(
        async (gesture: GestureName, service: ApiService, routeParams: Record<string, unknown>, data: unknown) => {
            emit("submitted", { gesture, surface: SURFACE })
            try {
                await apiCall(dispatch as never, service, { routeParams, data })
                emit("succeeded", { gesture, surface: SURFACE })
                return { ok: true as const }
            } catch (cause) {
                emit("failed", { gesture, surface: SURFACE }, cause)
                return { ok: false as const, failure: apiFailureOf(cause) }
            }
        },
        [dispatch, emit]
    )

    // ── Les enregistrements différés, un par clé ; ceux qui attendent partent à la fermeture ──
    const pendingRef = useRef(new Map<string, Pending>())
    const schedule = useCallback((key: string, delay: number, fire: () => void) => {
        const pending = pendingRef.current
        const current = pending.get(key)
        if (current != null) clearTimeout(current.timer)
        pending.set(key, {
            fire,
            timer: setTimeout(() => {
                pending.delete(key)
                fire()
            }, delay),
        })
    }, [])
    const cancel = useCallback((key: string) => {
        const current = pendingRef.current.get(key)
        if (current == null) return
        clearTimeout(current.timer)
        pendingRef.current.delete(key)
    }, [])
    const flush = useCallback(() => {
        const pending = [...pendingRef.current.values()]
        pendingRef.current.clear()
        for (const { timer, fire } of pending) {
            clearTimeout(timer)
            fire()
        }
    }, [])
    useEffect(() => flush, [flush])

    // Chaque mot envoyé prend un numéro : seule la réponse du dernier envoi d'une cible compte (une
    // réponse lente ne défait pas un envoi plus récent, ni un vocal supprimé entre-temps).
    const generationRef = useRef(new Map<string, number>())
    const nextGeneration = (target: CloseOutTarget) => {
        const key = targetKey(target)
        const next = (generationRef.current.get(key) ?? 0) + 1
        generationRef.current.set(key, next)
        return next
    }
    const isLatest = (target: CloseOutTarget, generation: number) =>
        generationRef.current.get(targetKey(target)) === generation

    // ── Le mot, sur un élève ou sur la plateforme ──
    const saveText = useCallback(
        (target: CloseOutTarget) => {
            const draft = draftOf(draftsRef.current, target)
            if (draft == null) return
            const generation = nextGeneration(target)
            const sent =
                target.kind === "student"
                    ? write(
                          "close_out.student_comment",
                          liveReportService.sendStudentComment,
                          { id: target.id },
                          {
                              tutor_comment: draft.text,
                          }
                      )
                    : write(
                          "close_out.platform_comment",
                          liveService.sendLiveQuality,
                          { id: occurrenceIdRef.current },
                          {
                              quality_score: draft.rating,
                              comment: draft.text,
                          }
                      )
            void sent.then((result) => {
                if (!isLatest(target, generation)) return
                if (result.ok) apply({ type: "sendSucceeded", target })
                else
                    apply({
                        type: "sendFailed",
                        target,
                        message: closeOutSendError(result.failure, TEXT_NOT_SAVED),
                        retry: "text",
                    })
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [apply, write]
    )

    const pendingVoiceRef = useRef(new Map<string, PendingVoice>())
    const sendVoice = useCallback(
        async (target: CloseOutTarget) => {
            const key = targetKey(target)
            const pending = pendingVoiceRef.current.get(key)
            if (pending == null) return
            const generation = nextGeneration(target)
            const gesture: GestureName =
                target.kind === "student" ? "close_out.student_voice" : "close_out.platform_voice"
            const fail = (cause: unknown) => {
                emit("failed", { gesture, surface: SURFACE }, cause)
                if (!isLatest(target, generation)) return
                apply({
                    type: "sendFailed",
                    target,
                    message: closeOutSendError(apiFailureOf(cause), VOICE_NOT_SENT),
                    retry: "voice",
                })
            }
            emit("submitted", { gesture, surface: SURFACE })
            try {
                if (pending.tmpKey == null) {
                    const blob = await fetchResourceFromURI(pending.file.uri)
                    const stored = await vaporStore(blob, {
                        baseURL: API_URL,
                        contentType: pending.file.mimeType,
                        verifyUpload: true,
                    })
                    if (!stored?.key) throw new Error("S3 upload returned no key")
                    pending.tmpKey = stored.key
                }
                // Effacé ou remplacé pendant le dépôt : le rattacher rendrait au serveur un vocal que le
                // tuteur a jeté, ou ferait gagner l'ancien sur le nouveau. Aucune issue à mesurer.
                if (!isLatest(target, generation)) return
                const draft = draftOf(draftsRef.current, target)
                if (target.kind === "student") {
                    const trial = isTrialOccurrence(occurrenceRef.current)
                    await apiCall(
                        dispatch as never,
                        trial ? liveReportService.setStudentDiagnostic : liveReportService.sendStudentComment,
                        {
                            routeParams: { id: target.id },
                            data: { tutor_comment: draft?.text ?? "", comment_record: [pending.tmpKey] },
                        }
                    )
                } else {
                    await apiCall(dispatch as never, liveService.sendLiveQuality, {
                        routeParams: { id: occurrenceIdRef.current },
                        data: {
                            quality_score: draft?.rating ?? 0,
                            comment: draft?.text ?? "",
                            live_satisfaction_record: [pending.tmpKey],
                        },
                    })
                }
            } catch (cause) {
                fail(cause)
                return
            }
            emit("succeeded", { gesture, surface: SURFACE })
            pendingVoiceRef.current.delete(key)
            if (isLatest(target, generation)) apply({ type: "sendSucceeded", target })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [apply, dispatch, emit]
    )

    const deleteVoice = useCallback(
        (target: CloseOutTarget) => {
            nextGeneration(target)
            pendingVoiceRef.current.delete(targetKey(target))
            if (target.kind === "student")
                void write(
                    "close_out.student_voice_delete",
                    liveReportService.deleteCommentRecord,
                    { id: target.id },
                    {}
                )
            else
                void write(
                    "close_out.platform_voice_delete",
                    liveService.deleteLiveSatisfactionRecord,
                    { id: occurrenceIdRef.current },
                    {}
                )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [write]
    )

    /** Changer de mode efface l'autre mot, sur le serveur aussi. */
    const switchMode = useCallback(
        (target: CloseOutTarget, mode: UITutorCommentMode) => {
            const draft = draftOf(draftsRef.current, target)
            if (draft == null || draft.mode === mode) return
            cancel(`${targetKey(target)}:text`)
            if (draft.mode === "voice" && draft.voice != null) deleteVoice(target)
            else if (draft.mode === "text" && draft.text !== "") {
                apply({ type: "setText", target, text: "" })
                saveText(target)
            }
            apply({ type: "setMode", target, mode })
        },
        [apply, cancel, deleteVoice, saveText]
    )

    const changeText = useCallback(
        (target: CloseOutTarget, text: string) => {
            apply({ type: "setText", target, text })
            schedule(`${targetKey(target)}:text`, TEXT_SAVE_MS, () => saveText(target))
        },
        [apply, schedule, saveText]
    )

    const retry = useCallback(
        (target: CloseOutTarget) => {
            const draft = draftOf(draftsRef.current, target)
            if (draft?.retry === "voice") {
                apply({ type: "sendStarted", target })
                void sendVoice(target)
            } else if (draft?.retry === "text") saveText(target)
        },
        [apply, saveText, sendVoice]
    )

    // ── L'enregistreur ──
    const [recorderTarget, setRecorderTarget] = useState<CloseOutTarget | null>(null)
    const openRecorder = useCallback(
        (target: CloseOutTarget) => {
            emit("opened", {
                gesture: target.kind === "student" ? "close_out.student_voice" : "close_out.platform_voice",
                surface: SURFACE,
            })
            setRecorderTarget(target)
        },
        [emit]
    )
    const onVoiceRecorded = useCallback(
        (file: VoiceFile) => {
            if (recorderTarget == null) return
            pendingVoiceRef.current.set(targetKey(recorderTarget), { file, tmpKey: null })
            apply({
                type: "voiceRecorded",
                target: recorderTarget,
                voice: { uri: file.uri, durationLabel: formatVoiceDuration(file.durationMs / 1000) },
            })
            void sendVoice(recorderTarget)
        },
        [recorderTarget, apply, sendVoice]
    )

    // ── La note et les étiquettes d'un élève ──
    const saveStudentNote = useCallback(
        (id: string) =>
            schedule(`student:${id}:note`, RATING_SAVE_MS, () => {
                const draft = draftOf(draftsRef.current, studentTarget(id))
                if (draft == null) return
                void write(
                    "close_out.student_rating",
                    liveReportService.sendStudentComment,
                    { id },
                    {
                        tutor_rating: draft.rating || null,
                        tutor_tags: draft.tags,
                    }
                )
            }),
        [schedule, write]
    )

    // ── La note et les étiquettes de la plateforme ──
    const ratePlatform = useCallback(
        (rating: number) => {
            const before = draftsRef.current.platform?.rating ?? 0
            apply({ type: "rate", target: PLATFORM, rating })
            const polarityChanged = platformTagsPositive(before) !== platformTagsPositive(rating)
            void write(
                "close_out.platform_rating",
                liveService.sendLiveQuality,
                { id: occurrenceIdRef.current },
                polarityChanged ? { quality_score: rating, live_satisfaction_tags: [] } : { quality_score: rating }
            )
        },
        [apply, write]
    )

    // ── Changer le mode du ressenti : avec un contenu, la confirmation de l'ancienne section ──
    const [platformModeConfirm, setPlatformModeConfirm] = useState<UITutorCommentMode | null>(null)

    // ── Le pied ──
    const [laterConfirmVisible, setLaterConfirmVisible] = useState(false)
    const back = useBackOr("menu")
    const inLive = useSelector((state: { liveMode?: { isLiveModeEnabled?: boolean } }) =>
        Boolean(state?.liveMode?.isLiveModeEnabled)
    )
    const leave = useCallback(() => {
        flush()
        // Les deux sorties quittent le live si le tuteur y est encore, comme l'ancien `CloseOutScreen`
        // (`leaveLiveAndGoBack`) : la clôture l'a déjà quitté, ceci ne rattrape qu'une sortie en échec —
        // revenir sans quitter ramènerait le tuteur dans la salle qu'il vient de clore. Ouvert depuis
        // l'accueil, pas de séance, rien à quitter.
        if (liveSessionId != null && inLive) {
            apiCall(dispatch as never, liveService.leaveLiveSession as ApiService, {
                routeParams: { id: liveSessionId },
            }).catch((cause: unknown) => emit("failed", { gesture: "close_out.leave_live", surface: SURFACE }, cause))
        }
        back()
    }, [flush, back, liveSessionId, inLive, dispatch, emit])

    // Les gestes lisent les brouillons et les catalogues par leurs refs : l'objet garde son identité
    // d'une frappe à l'autre, et ne change qu'avec la confirmation de « Plus tard ».
    const catalogsRef = useRef(catalogs)
    catalogsRef.current = catalogs
    const gestures = useMemo(() => {
        const student = (id: StudentId) => studentTarget(keyOf(id))
        return {
            onRateStudent: (id: StudentId, rating: number) => {
                apply({ type: "rate", target: student(id), rating })
                saveStudentNote(keyOf(id))
            },
            onToggleStudentTag: (id: StudentId, label: string) => {
                const draft = draftOf(draftsRef.current, student(id))
                const tag =
                    draft != null ? tagKeyOf(studentTagChoices(catalogsRef.current, draft.rating), label) : undefined
                if (tag == null) return
                apply({ type: "toggleTag", target: student(id), tag })
                saveStudentNote(keyOf(id))
            },
            onChangeStudentCommentMode: (id: StudentId, mode: UITutorCommentMode) => switchMode(student(id), mode),
            onChangeStudentCommentText: (id: StudentId, text: string) => changeText(student(id), text),
            onRecordStudentVoice: (id: StudentId) => openRecorder(student(id)),
            onDeleteStudentVoice: (id: StudentId) => {
                deleteVoice(student(id))
                apply({ type: "deleteVoice", target: student(id) })
            },
            onRetryStudentComment: (id: StudentId) => retry(student(id)),
            onStudentDone: (id: StudentId) => apply({ type: "done", target: student(id) }),
            onRatePlatform: ratePlatform,
            onTogglePlatformTag: (label: string) => {
                const rating = draftsRef.current.platform?.rating ?? 0
                const tag = tagKeyOf(platformTagChoices(catalogsRef.current, rating), label)
                if (tag == null) return
                apply({ type: "toggleTag", target: PLATFORM, tag })
                schedule("platform:tags", RATING_SAVE_MS, () => {
                    const draft = draftsRef.current.platform
                    if (draft == null) return
                    void write(
                        "close_out.platform_rating",
                        liveService.sendLiveQuality,
                        { id: occurrenceIdRef.current },
                        {
                            quality_score: draft.rating,
                            live_satisfaction_tags: draft.tags,
                        }
                    )
                })
            },
            onChangePlatformCommentMode: (mode: UITutorCommentMode) => {
                const draft = draftsRef.current.platform
                if (draft == null || draft.mode === mode) return
                const hasContent = draft.mode === "voice" ? draft.voice != null : draft.text !== ""
                if (hasContent) setPlatformModeConfirm(mode)
                else apply({ type: "setMode", target: PLATFORM, mode })
            },
            onChangePlatformCommentText: (text: string) => changeText(PLATFORM, text),
            onRecordPlatformVoice: () => openRecorder(PLATFORM),
            onDeletePlatformVoice: () => {
                deleteVoice(PLATFORM)
                apply({ type: "deleteVoice", target: PLATFORM })
            },
            onRetryPlatformComment: () => retry(PLATFORM),
            onFinish: () => {
                // La tâche « bilans manquants » de l'accueil se relit sur le compte.
                dispatch(apiRequest(authService.getUser) as never)
                leave()
            },
            onLater: () => setLaterConfirmVisible(true),
            onConfirmLater: () => {
                setLaterConfirmVisible(false)
                leave()
            },
            onCancelLater: () => setLaterConfirmVisible(false),
            laterConfirmVisible,
        }
    }, [
        apply,
        saveStudentNote,
        switchMode,
        changeText,
        openRecorder,
        deleteVoice,
        retry,
        ratePlatform,
        schedule,
        write,
        dispatch,
        leave,
        laterConfirmVisible,
    ])

    return {
        drafts,
        recorder:
            recorderTarget == null
                ? null
                : {
                      target: recorderTarget,
                      onSend: onVoiceRecorded,
                      onClose: () => setRecorderTarget(null),
                  },
        platformModeConfirm: {
            visible: platformModeConfirm != null,
            /** Ce qui sera supprimé : « enregistré » ou « écrit ». */
            spoken: draftsRef.current.platform?.mode === "voice",
            onConfirm: () => {
                if (platformModeConfirm != null) switchMode(PLATFORM, platformModeConfirm)
                setPlatformModeConfirm(null)
            },
            onCancel: () => setPlatformModeConfirm(null),
        },
        gestures,
    }
}
